import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { ProgressBarComponent } from '../progress-bar/progress-bar.component';
import { 
  AbstractControl, 
  FormControl, 
  FormsModule, 
  ReactiveFormsModule, 
  ValidationErrors, 
  Validators 
} from '@angular/forms';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { BiqButtonComponent } from '../biq-button/biq-button.component';
import { MatRadioModule } from '@angular/material/radio';
import { generateGraduationYear } from '@app/utils/sharedFunctions';
import { BIQ_TOTAL_QUESTIONS, JOB_OPTIONS, SCHOOL_NAMES, SCHOOL_TYPE, SCHOOL_YEAR } from '@app/utils/constants';
import { COURSE_MAJOR_CIP, MajorType } from '@app/utils/CIP-code';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { Observable, map, startWith } from 'rxjs';
import { AsyncPipe } from '@angular/common';
import { UserBiqData } from '@app/schemas/biq';
import { MatInputModule } from '@angular/material/input';
import { Store } from '@ngrx/store';
import { updateUserBiq } from '@app/state/actions/biq.action';
import { AuthService } from '@app/services/auth/auth.service';
import { TenantId } from '@app/enums/TenantId';

@Component({
  selector: 'app-biq-employment',
  imports: [
    ProgressBarComponent,
    BiqButtonComponent,
    FormsModule,
    MatSelectModule,
    MatFormFieldModule,
    MatCheckboxModule,
    MatRadioModule,
    MatInputModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    AsyncPipe,
  ],
  templateUrl: './employment.component.html',
  styleUrl: './employment.component.css'
})
export class EmploymentComponent implements OnInit {
  @Output() continueEmitter = new EventEmitter<void>();
  public questNo: number = 5;
  public totalQuestions: number = BIQ_TOTAL_QUESTIONS;
  public schoolYearOptions = SCHOOL_YEAR;
  public schoolGraduationOptions: { label: number | string, value: number | string }[] = [];
  public schoolNameOptions = SCHOOL_NAMES;
  public courseMajorOptions: MajorType[] = COURSE_MAJOR_CIP;
  public jobOptions = JOB_OPTIONS;
  public majorControl = new FormControl<MajorType | null>(null, [
    this.majorMatchValidator(this.courseMajorOptions),
  ]);
  public schoolYearControl = new FormControl('', [Validators.required]);
  public graduationControl = new FormControl('', [Validators.required]);
  public schoolNameControl = new FormControl('', [Validators.required]);
  public isWorking = new FormControl(false);
  public jobControl = new FormControl<null | 'part_time' | 'full_time'>(null);
  public filteredMajorOptions!: Observable<MajorType[]>; 
  public storedBiqData!: UserBiqData;
  public isAUTenant: boolean = false;

  constructor(
    private store: Store<{ userBiq: UserBiqData }>,
    private authService: AuthService
  ) {
    // Check if tenant is American University
    const tenantId = this.authService.getCurrentUser()?.tenantId;
    this.isAUTenant = tenantId === TenantId.AU || tenantId === TenantId.AU_MOCK;
    
    // Auto-set school name for AU tenants
    if (this.isAUTenant) {
      this.schoolNameControl.setValue('American University');
      this.schoolNameControl.clearValidators();
      this.schoolNameControl.updateValueAndValidity();
    }

    this.store.select('userBiq').subscribe(value => {
      this.storedBiqData = value;
      if (value) {
        if (value.grade) this.schoolYearControl.setValue(value.grade);
        if (value.graduationYear) this.graduationControl.setValue(value.graduationYear);
        if (value.schoolName) this.schoolNameControl.setValue(value.schoolName);
        if (value.major) {
          const majorOption = this.courseMajorOptions.find(m => m.value === value.major);
          if (majorOption) this.majorControl.setValue(majorOption);
        }
        if (value.isWorking !== undefined) this.isWorking.setValue(value.isWorking);
        if (value.jobType) this.jobControl.setValue(value.jobType);
      }
    });
  }

  ngOnInit() {
    this.schoolGraduationOptions = generateGraduationYear();
    this.schoolGraduationOptions.push({ label: 'Other', value: 'Other' });

    this.filteredMajorOptions = this.majorControl.valueChanges.pipe(
      startWith(''),
      map(value => {
        const filterValue = typeof value === 'string' ? value : value?.label ?? '';
        return this._filterMajorOption(filterValue);
      })
    );
  }

  private _filterMajorOption(value: string): MajorType[] {
    const filterVal = value.toLowerCase();
    return this.courseMajorOptions.filter(
      loc => loc.label.toLowerCase().includes(filterVal)
    );
  }

  private majorMatchValidator(validOptions: MajorType[]) {
    return (control: AbstractControl): ValidationErrors | null => {
      const inputValue = control.value;
      if (!inputValue) return null;
  
      const match = validOptions.find(e => e.value == inputValue.value);
      return match ? null : { invalidMajor: true };
    };
  }

  displayMajorLabel = (major: MajorType): string => {
    return major?.label ?? '';
  };

  onClickContinue() {
    this.majorControl.markAsTouched();
    this.schoolYearControl.markAsTouched();
    this.graduationControl.markAsTouched();
    if (!this.isAUTenant) {
      this.schoolNameControl.markAsTouched();
    }

    if (
      this.majorControl.invalid || this.schoolYearControl.invalid || 
      this.graduationControl.invalid || (!this.isAUTenant && this.schoolNameControl.invalid)
    ) return;

    const updatedBiq = {
      ...this.storedBiqData,
      grade: this.schoolYearControl.value!,
      graduationYear: this.graduationControl.value!,
      schoolName: this.schoolNameControl.value!,
      major: this.majorControl.value?.value!,
      isWorking: this.isWorking.value!,
    }
    
    if (this.isWorking.value) {
      updatedBiq.jobType = this.jobControl.value!;
    }
    this.store.dispatch(updateUserBiq({ user: updatedBiq }));
    this.continueEmitter.emit();
  }
}
